import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * Every deploy gives changed JS chunks a new content hash and the OLD hashed
 * files stop existing on the CDN. A browser tab that's had the SPA open
 * across a deploy (no full reload) still has the OLD chunk map baked into its
 * already-loaded bundle — so a client-side navigation to a route whose chunk
 * changed tries to `import()` a file that's gone.
 *
 * Deliberately NOT auto-reloading: an earlier version of this fix called
 * `window.location.reload()` automatically on the first hit, and under load
 * testing (a chunk permanently 404ing, simulating a genuinely broken deploy
 * rather than a momentarily-stale one) that produced a real reload loop —
 * dozens of reloads per second, not a graceful one-time retry. A manual
 * "Reload" button is slightly less invisible for the common transient case,
 * but it can never loop, which matters far more.
 */

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return /dynamically imported module|importing a module script failed|loading chunk/i.test(message)
}

/** Shown when a page's JS chunk fails to load — resolved directly in
 *  router.tsx's `page()` helper via a `.catch()` on the loader promise (a
 *  rejected `React.lazy()` import does not reliably surface as a catchable
 *  render error here — verified empirically; neither a router `errorElement`
 *  nor a class boundary around `<Suspense>` ever fired for it). */
export function StaleChunkFallback() {
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-4 text-center">
      <h1 className="text-2xl font-black uppercase tracking-tight">Update Available</h1>
      <p className="mt-3 text-white/60">
        This site was just updated and this page needs a quick reload to load the new version.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="mt-6 rounded-md bg-[var(--wg-primary)] px-4 py-2 text-sm font-bold uppercase tracking-wide text-black"
      >
        Reload
      </button>
    </div>
  )
}

interface State {
  error: Error | null
}

/** Catch-all for ordinary runtime errors thrown by an already-loaded page's
 *  own render (ex: a real bug in that page) — a different failure mode from
 *  the stale-chunk case above, same general shape of fallback UI. */
export class RouteErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[RouteErrorBoundary]', error, info.componentStack)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    if (isChunkLoadError(error)) return <StaleChunkFallback />

    return (
      <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-4 text-center">
        <h1 className="text-2xl font-black uppercase tracking-tight">Something Went Wrong</h1>
        <p className="mt-3 text-white/60">An unexpected error occurred loading this page.</p>
        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md bg-[var(--wg-primary)] px-4 py-2 text-sm font-bold uppercase tracking-wide text-black"
          >
            Reload
          </button>
          <a
            href="/"
            className="rounded-md border border-white/20 px-4 py-2 text-sm font-bold uppercase tracking-wide text-white hover:border-white/40"
          >
            Go Home
          </a>
        </div>
      </div>
    )
  }
}
